import type { QueryClient } from "@tanstack/react-query";
import { createRootRouteWithContext, Outlet } from "@tanstack/react-router";
import { ErrorBoundary } from "@/components/ui/error-boundary";
import { ImpersonationBanner } from "@/features/admin/impersonation-banner";
import { AuthProvider } from "@/features/auth/auth-provider";
import { useAuth } from "@/features/auth/use-auth";
import { TenantSwitcherProvider, useTenantSwitcher } from "@/features/tenant-switcher";
import { OrgGroupRulesSync } from "@/features/visibility/editor/org-group-rules-sync";
import {
	useMyOrgVisibilityRules,
	useMyVisibilityRules,
	useUpsertOrgGroupVisibilityRule,
	useUpsertVisibilityRule,
	useUpsertVisibilityRuleForAllRoles,
	useVisibilityRules,
} from "@/features/visibility/use-visibility-query";
import { VisibilityProvider } from "@/features/visibility/visibility-provider";
import { FavoritesProvider } from "@/hooks/use-favorites";
import { ThemeProvider } from "@/hooks/use-theme";
import type { Role } from "@/lib/auth-types";

function VisibilityProviderConnected({ children }: { children: React.ReactNode }) {
	const { user } = useAuth();
	const { selectedTenantId } = useTenantSwitcher();
	const isSuperAdmin = user?.roles?.includes("super_admin") ?? false;
	const isAuthenticated = !!user;

	const { data: myRules = [] } = useMyVisibilityRules(
		selectedTenantId ?? undefined,
		isAuthenticated,
	);
	const { data: allRules } = useVisibilityRules(
		selectedTenantId ?? undefined,
		isAuthenticated && isSuperAdmin,
	);

	const { data: orgData } = useMyOrgVisibilityRules(selectedTenantId ?? undefined, isAuthenticated);
	const myOrgRules = orgData?.data ?? [];
	const userOrgGroups = orgData?.userGroups ?? [];

	const upsertMutation = useUpsertVisibilityRule();
	const upsertAllMutation = useUpsertVisibilityRuleForAllRoles();
	const upsertOrgGroupMutation = useUpsertOrgGroupVisibilityRule();

	const userRoles = user?.roles ?? [];

	const rules = isSuperAdmin && allRules != null ? allRules : myRules;

	function handleToggleVisibility(role: Role, elementId: string, nextVisible: boolean) {
		console.debug("[visibility] toggle", { role, elementId, nextVisible, selectedTenantId });
		upsertMutation.mutate(
			{ role, elementId, visible: nextVisible },
			{
				onError: (err) =>
					console.error("[visibility] upsert FAILED", { role, elementId, nextVisible, err }),
			},
		);
	}

	function handleToggleOrgGroupVisibility(
		groupId: number,
		elementId: string,
		nextVisible: boolean,
	) {
		console.debug("[visibility] toggle org group", {
			groupId,
			elementId,
			nextVisible,
			selectedTenantId,
		});
		upsertOrgGroupMutation.mutate(
			{ groupId, elementId, visible: nextVisible },
			{
				onError: (err) =>
					console.error("[visibility] org group upsert FAILED", {
						groupId,
						elementId,
						nextVisible,
						err,
					}),
			},
		);
	}

	function handleToggleVisibilityForAll(elementId: string, nextVisible: boolean) {
		console.debug("[visibility] toggle ALL", { elementId, nextVisible, selectedTenantId });
		upsertAllMutation.mutate(
			{ elementId, visible: nextVisible },
			{
				onError: (err) =>
					console.error("[visibility] bulk upsert FAILED", { elementId, nextVisible, err }),
			},
		);
	}

	return (
		<VisibilityProvider
			rules={rules}
			orgRules={myOrgRules}
			userRoles={userRoles}
			userOrgGroups={userOrgGroups}
			onToggleVisibility={handleToggleVisibility}
			onToggleVisibilityForAll={handleToggleVisibilityForAll}
			onToggleOrgGroupVisibility={handleToggleOrgGroupVisibility}
		>
			{/* Musi być WEWNĄTRZ providera — dopiero stąd widać `selectedGroupId`. */}
			<OrgGroupRulesSync />
			{children}
		</VisibilityProvider>
	);
}

interface RouterContext {
	queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
	component: RootLayout,
});

function RootLayout() {
	return (
		<ErrorBoundary>
			<ThemeProvider>
				<AuthProvider>
					<TenantSwitcherProvider>
						<VisibilityProviderConnected>
							<FavoritesProvider>
								<ImpersonationBanner />
								<ErrorBoundary>
									<Outlet />
								</ErrorBoundary>
							</FavoritesProvider>
						</VisibilityProviderConnected>
					</TenantSwitcherProvider>
				</AuthProvider>
			</ThemeProvider>
		</ErrorBoundary>
	);
}
